import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  Merge,
  X,
  CheckCircle2,
  Link2,
  Music,
  History,
  Undo2,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Pagination } from "../../components/ui/Pagination";
import { Badge } from "../../components/ui/Badge";
import { Modal, ModalContent, ModalFooter } from "../../components/ui/Modal";
import { FlaggerMultiSelect, useFlaggerFilter } from "../../components/ui/FlaggerMultiSelect";
import {
  ComparisonGrid,
  DuplicateReviewCard,
  MergeResolutionBadge,
  MergedDuplicateCard,
  ResolutionDetails,
  ReviewEventLink,
  formatReviewDateTime,
} from "../../components/ui/DuplicateReview";
import { SearchFilterForm, useSearchFilter } from "../../components/ui/SearchFilterForm";
import { useReviewQueueCount } from "../../hooks/useReviewQueueCount";
import { ProviderLabel } from "../../components/ui/ProviderLabel";
import { artistsApi } from "../../utils/api";
import { displayLabel, definedEntries } from "../../utils/helpers";

function ArtistCard({ artist, isSelected, onSelect, selectLabel }) {
  const sourceIds = Object.fromEntries(definedEntries(artist.source_ids));
  const genres = (artist.genres || []).map((g) => g.name || g).filter(Boolean);
  const links = artist.links || {};
  // `events` is a preview of the latest-dated ones; `event_count` is the exact total the API
  // counted across every status and date.
  const events = artist.events || [];
  const hiddenEventCount = Math.max((artist.event_count ?? 0) - events.length, 0);

  return (
    <div
      className={`min-w-0 rounded-lg border p-4 transition-colors ${
        isSelected ? "border-primary bg-primary/5" : "border-border bg-card"
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <Music className="h-4 w-4 shrink-0 text-muted-foreground" />
        <h4 className="min-w-0 flex-1 truncate text-sm font-semibold">
          {displayLabel(artist.preferred_name, artist.name)}
        </h4>
        {artist.has_musicbrainz ? (
          <Badge variant="default" className="shrink-0 text-[10px]">MusicBrainz ✓</Badge>
        ) : (
          <Badge variant="secondary" className="shrink-0 text-[10px]">no MBID</Badge>
        )}
      </div>

      <ProviderLabel preferred={artist.preferred_name} original={artist.name} className="mb-2 truncate" />

      <div className="space-y-1.5 text-xs text-muted-foreground">
        {Object.keys(sourceIds).length > 0 && (
          <div className="flex flex-wrap gap-1">
            {Object.entries(sourceIds).map(([provider, value]) => (
              <span key={provider} className="rounded bg-muted px-1.5 py-0.5 text-[10px]">
                {provider}: <span className="font-mono">{String(value).slice(0, 16)}</span>
              </span>
            ))}
          </div>
        )}
        {(artist.country || artist.popularity != null) && (
          <p>
            {artist.country ? `Country: ${artist.country}` : ""}
            {artist.country && artist.popularity != null ? " · " : ""}
            {artist.popularity != null ? `Popularity: ${artist.popularity}` : ""}
          </p>
        )}
        {genres.length > 0 && <p className="truncate">Genres: {genres.join(", ")}</p>}
      </div>

      {Object.keys(links).length > 0 && (
        <div className="mt-2 space-y-0.5 border-t border-border pt-2">
          {Object.entries(links).map(([key, url]) => (
            <div key={key} className="flex min-w-0 items-center gap-1.5 text-[11px]">
              <Link2 className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="w-16 shrink-0 truncate text-muted-foreground">{key}</span>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 flex-1 truncate text-blue-600 hover:underline"
                title={url}
              >
                {url}
              </a>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 border-t border-border pt-2">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Events ({artist.event_count ?? 0})
        </p>
        {events.length === 0 ? (
          <p className="text-xs text-muted-foreground">No referenced events</p>
        ) : (
          <ul className="max-h-48 space-y-1.5 overflow-y-auto text-xs">
            {events.map((ev) => (
              <ReviewEventLink key={ev.id || ev.title} event={ev} />
            ))}
            {hiddenEventCount > 0 && (
              <li className="text-[10px] italic text-muted-foreground">
                +{hiddenEventCount} more not shown
              </li>
            )}
          </ul>
        )}
      </div>

      {onSelect && (
        <Button
          variant={isSelected ? "default" : "outline"}
          size="sm"
          className="mt-3 w-full text-xs"
          onClick={() => onSelect(artist.id)}
        >
          {isSelected && <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
          {selectLabel}
        </Button>
      )}
    </div>
  );
}

function ArtistPair({ group, onActionSuccess }) {
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [canonicalId, setCanonicalId] = useState(group.canonical_hint_id || null);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeError, setMergeError] = useState(null);
  const [markLoading, setMarkLoading] = useState(false);
  const [markError, setMarkError] = useState(null);

  const [artistA, artistB] = group.artists || [];
  if (!artistA || !artistB) return null;
  const hasMbidPriority = Boolean(group.canonical_hint_id || artistA.has_musicbrainz || artistB.has_musicbrainz);

  const openMergeModal = () => {
    setCanonicalId(group.canonical_hint_id || artistB.id);
    setMergeError(null);
    setMergeModalOpen(true);
  };

  const handleConfirmMerge = async () => {
    const sourceId = group.artists.find((a) => a.id !== canonicalId)?.id;
    if (!sourceId || !canonicalId) return;
    setMergeLoading(true);
    setMergeError(null);
    try {
      await artistsApi.mergeInto(sourceId, canonicalId);
      setMergeModalOpen(false);
      await onActionSuccess?.("Artists merged");
    } catch (e) {
      setMergeError(e?.response?.data?.detail || "Merge failed");
    } finally {
      setMergeLoading(false);
    }
  };

  const handleMarkDifferent = async () => {
    // Resolve the pair as "different" — these are not the same artist. A resolved pair is never
    // re-flagged by future detection runs.
    setMarkLoading(true);
    setMarkError(null);
    try {
      await artistsApi.markDifferent(artistA.id, artistB.id);
      await onActionSuccess?.("Marked as different");
    } catch (e) {
      setMarkError(e?.response?.data?.detail || "Could not mark as different");
    } finally {
      setMarkLoading(false);
    }
  };

  return (
    <DuplicateReviewCard
      title={
        <>
            {displayLabel(artistA.preferred_name, artistA.name)}{" "}
            <span className="font-normal text-muted-foreground">vs</span>{" "}
            {displayLabel(artistB.preferred_name, artistB.name)}
            {hasMbidPriority && (
              <Badge variant="default" className="ml-2 align-middle text-[10px]">
                MBID priority
              </Badge>
            )}
        </>
      }
      reason={group.reason}
      score={group.similarity_score}
      scoreDestructiveAt={95}
      scoreWarningAt={90}
    >
      <ComparisonGrid left={<ArtistCard artist={artistA} />} right={<ArtistCard artist={artistB} />} />

          <div className="flex flex-col gap-2 pt-1 sm:flex-row">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={openMergeModal}>
              <Merge className="h-3.5 w-3.5" />
              Merge (same artist)
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-muted-foreground sm:ml-auto"
              onClick={handleMarkDifferent}
              disabled={markLoading}
            >
              {markLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
              Different (not the same artist)
            </Button>
          </div>
          {markError && <p className="text-sm text-destructive">{markError}</p>}

      <Modal open={mergeModalOpen} onOpenChange={setMergeModalOpen}>
        <ModalContent
          title="Merge artists — pick the canonical one"
          description="The canonical artist stays active; the other's events, users, source ids, links and genres are absorbed into it, then the source artist is kept as a duplicate record. When exactly one artist has a MusicBrainz id, that artist is suggested as canonical."
        >
          <div className="mt-2 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2">
            <ArtistCard
              artist={artistA}
              isSelected={canonicalId === artistA.id}
              onSelect={setCanonicalId}
              selectLabel="Keep this one (canonical)"
            />
            <ArtistCard
              artist={artistB}
              isSelected={canonicalId === artistB.id}
              onSelect={setCanonicalId}
              selectLabel="Keep this one (canonical)"
            />
          </div>
          {mergeError && <p className="mt-3 text-sm text-destructive">{mergeError}</p>}
          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => setMergeModalOpen(false)} disabled={mergeLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirmMerge} disabled={!canonicalId || mergeLoading}>
              {mergeLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Merge className="mr-2 h-4 w-4" />}
              Confirm merge
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </DuplicateReviewCard>
  );
}

const MERGE_TYPE_VARIANT = { admin_review: "default", mbid_change: "secondary", auto: "warning" };
const MERGE_TYPE_LABEL = { admin_review: "Admin merge", mbid_change: "Admin MBID change", auto: "Automatic" };

function ArtistResolutionDetails({ resolution }) {
  return (
    <ResolutionDetails
      resolution={resolution}
      mergeTypeLabels={MERGE_TYPE_LABEL}
      mergeTypeVariants={MERGE_TYPE_VARIANT}
    >
      <span>{resolution?.users_repointed || 0} users repointed</span>
    </ResolutionDetails>
  );
}

function MergedArtistPair({ pair, onUndone }) {
  const duplicate = pair.duplicate_artist;
  const canonical = pair.canonical_artist;
  const duplicateName = displayLabel(duplicate?.preferred_name, duplicate?.name) || duplicate?.id;
  const [undoOpen, setUndoOpen] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [undoError, setUndoError] = useState(null);

  // A previous attempt's error belongs to that attempt.
  const openUndo = () => {
    setUndoError(null);
    setUndoOpen(true);
  };

  const handleUndo = async () => {
    setUndoLoading(true);
    setUndoError(null);
    try {
      const result = await artistsApi.undoMerge(duplicate.id);
      setUndoOpen(false);
      onUndone?.(result.message);
    } catch (e) {
      // A 422's `detail` is a list of objects, which React cannot render.
      const detail = e?.response?.data?.detail;
      setUndoError(typeof detail === "string" ? detail : "Undo failed");
    } finally {
      setUndoLoading(false);
    }
  };
  const missingCanonical = (
    <div className="flex min-w-0 items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 p-4 text-xs italic text-muted-foreground">
      Canonical artist no longer exists
    </div>
  );

  const card = (
    <MergedDuplicateCard
      icon={<Merge className="h-4 w-4 shrink-0 text-muted-foreground" />}
      title={
        <>
          <span className="text-red-700">{duplicateName}</span>
          <span className="mx-1.5 font-normal text-muted-foreground">→</span>
          <span>{displayLabel(canonical?.preferred_name, canonical?.name) || canonical?.id || "missing canonical"}</span>
        </>
      }
      meta={
        <>
          <MergeResolutionBadge resolution={pair.resolution} />
          {pair.resolution?.resolved_at && (
            <span className="text-xs text-muted-foreground">{formatReviewDateTime(pair.resolution.resolved_at)}</span>
          )}
        </>
      }
      action={
        // A merge made before the API recorded what an undo needs cannot be undone (409).
        canonical && duplicate?.undoable && (
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={openUndo}>
            <Undo2 className="h-3.5 w-3.5" />
            Undo
          </Button>
        )
      }
    >
      <ArtistResolutionDetails resolution={pair.resolution} />
      <ComparisonGrid left={<ArtistCard artist={duplicate} />} right={canonical ? <ArtistCard artist={canonical} /> : missingCanonical} separator="→" />
    </MergedDuplicateCard>
  );

  return (
    <>
      {card}
      <Modal open={undoOpen} onOpenChange={setUndoOpen}>
        <ModalContent
          title="Undo artist merge"
          description={`This will separate "${duplicateName}" from the artist it was merged into and put the pair back in review.`}
        >
          <div className="mt-3 space-y-1 rounded-lg border border-yellow-200 bg-yellow-50 px-3 py-2.5 text-sm text-yellow-800">
            <p className="font-medium">What will happen</p>
            <ul className="list-inside list-disc space-y-0.5 text-xs">
              <li><span className="font-medium">{duplicateName}</span> becomes an active artist again</li>
              <li>Every event the merge moved, past ones included, and the profiles it moved go back to it</li>
              <li>The ids, links, genres and images the canonical took from it are removed from the canonical</li>
              <li>The pair goes back to the review queue, to merge again or mark different; the automatic merge leaves it alone meanwhile</li>
            </ul>
            <p className="pt-1 text-xs">
              Users&apos; like counts, their removals of this artist from their profiles, and events added to
              the canonical artist since the merge stay on the canonical artist.
            </p>
          </div>
          {undoError && <p className="mt-3 text-sm text-destructive">{undoError}</p>}
          <ModalFooter className="mt-4">
            <Button variant="outline" onClick={() => setUndoOpen(false)} disabled={undoLoading}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleUndo} disabled={undoLoading}>
              {undoLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}
              Undo merge
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function MergedArtistsPanel({ onPairReopened }) {
  const [pairs, setPairs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const { searchInput, setSearchInput, search, submitSearch, clearSearch } = useSearchFilter();
  const {
    flaggers,
    selectedFlaggerIds: flaggedBySelection,
    setSelectedFlaggerIds: setFlaggedBySelection,
    flaggedByParam,
    syncFlaggers,
  } = useFlaggerFilter();

  const load = useCallback(async (pageNum = 1, searchTerm = "", flaggedBy = "") => {
    setLoading(true);
    setError(null);
    try {
      const data = await artistsApi.getFlaggedDuplicates(pageNum, 20, searchTerm || null, flaggedBy || null);
      setPairs(data.pairs || []);
      setPagination(data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 });
      syncFlaggers(data.flaggers);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load merged artists.");
    } finally {
      setLoading(false);
    }
  }, [syncFlaggers]);

  useEffect(() => {
    if (flaggedByParam === null) {
      setPairs([]);
      setPagination({ page: 1, page_size: 20, total: 0, total_pages: 1 });
      setLoading(false);
      return;
    }
    load(page, search, flaggedByParam);
  }, [load, page, search, flaggedByParam]);

  // An undo's notice describes that undo; any later navigation is a different view.
  const changePage = (next) => {
    setNotice(null);
    setPage(next);
  };

  const handleSearchSubmit = (e) => {
    changePage(1);
    submitSearch(e);
  };

  const handleSearchClear = () => {
    changePage(1);
    clearSearch();
  };

  const handleFlaggedByChange = (next) => {
    changePage(1);
    setFlaggedBySelection(next);
  };

  const handleUndone = (message) => {
    setNotice(message);
    // The pair is back in the review queue: its list and the tab badge follow.
    onPairReopened?.();
    // The last pair on a page past the first: step back rather than reload into an empty page.
    if (pairs.length === 1 && page > 1) setPage(page - 1);
    else load(page, search, flaggedByParam);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Merged artists</h2>
          <p className="text-sm text-muted-foreground">
            Artists retained as duplicates after their events, users, source ids, links, and genres were absorbed into
            the canonical artist.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <SearchFilterForm
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={handleSearchSubmit}
            onClear={handleSearchClear}
            placeholder="Search by artist name..."
            loading={loading}
            submitVariant="default"
            clearContent="Clear"
            inputClassName="sm:w-56"
          >
            <FlaggerMultiSelect
              flaggers={flaggers}
              selected={flaggedBySelection}
              onChange={handleFlaggedByChange}
            />
          </SearchFilterForm>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm">{notice}</div>
      )}

      {!loading && (
        <>
          <div className="text-sm text-muted-foreground">
            {pagination.total === 0
              ? "No artists marked as duplicates"
              : `${pagination.total} artist${pagination.total !== 1 ? "s" : ""} marked as duplicate`}
            {search && <span> matching &ldquo;{search}&rdquo;</span>}
            {flaggedByParam === null && <span> · no flaggers selected</span>}
            {flaggedByParam && (
              <span>
                {" · flagged by "}
                {flaggedBySelection.length === 1
                  ? flaggers.find((f) => f.id === flaggedBySelection[0])?.label || flaggedBySelection[0]
                  : `${flaggedBySelection.length} selected`}
              </span>
            )}
          </div>

          {pairs.length > 0 ? (
            <div className="space-y-3">
              {pairs.map((pair) => (
                <MergedArtistPair
                  key={pair.duplicate_artist?.id}
                  pair={pair}
                  onUndone={handleUndone}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
              <History className="mx-auto mb-3 h-8 w-8" />
              <p className="font-medium">No merged artists yet</p>
              <p className="mt-1 text-sm">Duplicate artist records will appear here.</p>
            </div>
          )}

          <Pagination page={page} totalPages={pagination.total_pages} onPageChange={changePage} />
        </>
      )}
    </div>
  );
}

export default function ArtistDuplicatesView({ onQueueChange, refreshSignal = 0 }) {
  const [view, setView] = useState("pairs");
  const [groups, setGroups] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 20, total: 0, total_pages: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const { searchInput, setSearchInput, search, submitSearch, clearSearch } = useSearchFilter();
  const fetchQueueCount = useCallback(() => artistsApi.getDuplicates(1, 1), []);
  const { claim: claimQueueCount, refresh: refreshQueueCount } = useReviewQueueCount(fetchQueueCount, onQueueChange);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const loadDuplicates = useCallback(
    async (pageNum = 1, searchTerm = "") => {
      // Claim the badge slot before the request, so a slow earlier load can't overwrite a
      // newer count. A filtered load never publishes: `total_groups` is filtered too.
      const publishCount = searchTerm ? null : claimQueueCount();
      setLoading(true);
      setError(null);
      setScanned(false);
      try {
        const data = await artistsApi.getDuplicates(pageNum, 20, searchTerm || null);
        setGroups(data.groups || []);
        setPagination(data.pagination || { page: 1, page_size: 20, total: 0, total_pages: 0 });
        publishCount?.(data.total_groups);
        setScanned(true);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load artist duplicate pairs.");
      } finally {
        setLoading(false);
      }
    },
    [claimQueueCount]
  );

  useEffect(() => {
    loadDuplicates(page, search);
    // refreshSignal bumps when the parent resolves/auto-merges a pair via the MusicBrainz flow.
  }, [loadDuplicates, page, search, refreshSignal]);

  const handleSearchSubmit = (e) => {
    setPage(1);
    submitSearch(e);
  };

  const handleSearchClear = () => {
    setPage(1);
    clearSearch();
  };

  return (
    <div className="space-y-6">
      <div className="inline-flex rounded-lg border border-border p-0.5">
        <button
          type="button"
          onClick={() => setView("pairs")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            view === "pairs" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          Open pairs
        </button>
        <button
          type="button"
          onClick={() => setView("history")}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            view === "history" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <History className="h-3.5 w-3.5" />
          Merged artists
        </button>
      </div>

      {view === "history" ? (
        <MergedArtistsPanel
          onPairReopened={() => {
            loadDuplicates(page, search);
            // The unfiltered count, as after a merge: a search-filtered total is not the badge.
            refreshQueueCount();
          }}
        />
      ) : (
        <>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Possible duplicate artists</h2>
          <p className="text-sm text-muted-foreground">
            Similar-name pairs where at least one artist does not have a MusicBrainz id. Pairs with one MusicBrainz id
            are listed first.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <SearchFilterForm
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={handleSearchSubmit}
            onClear={handleSearchClear}
            placeholder="Search by artist name..."
            loading={loading}
            submitVariant="default"
            clearContent="Clear"
            inputClassName="sm:w-56"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {toast && (
        <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800">
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          {toast}
        </div>
      )}

      {loading && (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin" />
          <p className="font-medium">Loading artist duplicate pairs…</p>
        </div>
      )}

      {!loading && scanned && (
        <>
          <div className="text-sm text-muted-foreground">
            {groups.length === 0
              ? "No artist duplicate pairs stored"
              : `${groups.length} pair${groups.length !== 1 ? "s" : ""} on this page · ${pagination.total} total`}
          </div>

          {groups.length > 0 && (
            <div className="space-y-3">
              {groups.map((group) => (
                <ArtistPair
                  key={group.id}
                  group={group}
                  onActionSuccess={(msg) => {
                    showToast(msg);
                    loadDuplicates(page, search);
                    // Re-read the unfiltered count: decrementing `pagination.total` would
                    // publish a search-filtered number as the app-wide badge.
                    refreshQueueCount();
                  }}
                />
              ))}
            </div>
          )}

          <Pagination page={page} totalPages={pagination.total_pages} onPageChange={setPage} />

          {groups.length === 0 && (
            <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
              <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-green-500" />
              <p className="font-medium">All clear</p>
              <p className="mt-1 text-sm">No possible duplicate artists detected.</p>
            </div>
          )}
        </>
      )}
        </>
      )}
    </div>
  );
}
